import { FormEventHandler, useEffect, useState } from "react"
import { Link, router, useForm } from "@inertiajs/react"
import { AppLayout } from "../../components/AppLayout"
import { Badge } from "../../components/Badge"
import { CardEditorNav } from "../../components/CardEditorNav"
import { Field } from "../../components/Field"
import { GalleryLightbox } from "../../components/GalleryLightbox"
import { SectionCard } from "../../components/SectionCard"
import { TextInput } from "../../components/TextInput"

type GalleryImage = {
  id: number
  url: string
  position: number
  category: string
  description: string | null
}

type Props = {
  card: {
    id: number
    title: string
    slug: string
    public_url: string
    preview_url: string
  }
  gallery_images: GalleryImage[]
  customer_mode?: boolean
}

export default function Gallery({ card, gallery_images, customer_mode: customerMode = false }: Props) {
  const editorUrl = customerMode ? `/dash/card/${card.id}/edit/media` : `/admin/cards/${card.id}/edit/media`
  const previewUrl = customerMode ? `/dash/card/${card.id}/preview` : card.preview_url
  const galleryPath = customerMode ? `/dash/card/${card.id}/gallery` : `/admin/cards/${card.id}/gallery`
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [categoryDrafts, setCategoryDrafts] = useState<Record<number, string>>({})
  const [descriptionDrafts, setDescriptionDrafts] = useState<Record<number, string>>({})
  const [selectedPreviews, setSelectedPreviews] = useState<string[]>([])
  const { data, setData, post, processing } = useForm({
    images: [] as File[],
    category: "כללי",
    description: "",
  })

  useEffect(() => {
    setCategoryDrafts(Object.fromEntries(gallery_images.map((image) => [image.id, image.category])))
    setDescriptionDrafts(Object.fromEntries(gallery_images.map((image) => [image.id, image.description ?? ""])))
  }, [gallery_images])

  useEffect(() => {
    const urls = data.images.map((file) => URL.createObjectURL(file))
    setSelectedPreviews(urls)
    return () => urls.forEach((url) => URL.revokeObjectURL(url))
  }, [data.images])

  const removeSelectedImage = (index: number) => {
    setData("images", data.images.filter((_, currentIndex) => currentIndex !== index))
  }

  const submit: FormEventHandler = (event) => {
    event.preventDefault()
    post(galleryPath, {
      forceFormData: true,
      preserveScroll: true,
    })
  }

  return (
    <AppLayout title={`${card.title} | גלריה`} customerMode={customerMode}>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <h1 className="text-3xl font-bold">תמונות בגלריה</h1>
            <Badge tone="slate">{card.slug}</Badge>
          </div>
          <p className="text-slate-600">{card.title}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link href={previewUrl} className="btn-secondary">תצוגה</Link>
          <a href={card.public_url} className="btn-secondary" target="_blank" rel="noreferrer">פתיחת הכרטיס</a>
          <Link href={editorUrl} className="btn-secondary">חזרה לעריכה</Link>
        </div>
      </div>

      <CardEditorNav cardId={card.id} customerMode={customerMode} activeSection="gallery" analyticsHref={customerMode ? `/dash/card/${card.id}/analytics` : `/admin/cards/${card.id}/analytics`} />

      <div className="grid gap-6">
        <SectionCard title="העלאת תמונות" description="אפשר להוסיף כמה תמונות יחד">
          <form onSubmit={submit} className="space-y-4">
            <Field label="תמונות" hint="אפשר לבחור כמה קבצים">
              <TextInput
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setData("images", Array.from(e.target.files ?? []))}
              />
            </Field>
            {selectedPreviews.length > 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="text-sm font-medium text-slate-800">תצוגה מקדימה לפני העלאה</div>
                  <div className="text-xs text-slate-500">{selectedPreviews.length} תמונות נבחרו</div>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {selectedPreviews.map((url, index) => (
                    <div key={`${url}-${index}`} className="group relative aspect-square overflow-hidden rounded-2xl bg-white shadow-sm">
                      <img src={url} alt={data.images[index]?.name ?? ""} className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeSelectedImage(index)}
                        className="absolute end-2 top-2 rounded-full bg-slate-900/75 px-2 py-1 text-xs text-white opacity-0 transition group-hover:opacity-100 focus:opacity-100"
                        aria-label={`הסרת ${data.images[index]?.name ?? "תמונה"}`}
                      >
                        הסרה
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <Field label="קטגוריה" hint="הקטגוריה תחול על כל התמונות שיועלו עכשיו">
              <TextInput maxLength={80} value={data.category} onChange={(e) => setData("category", e.target.value)} placeholder="למשל: תפריט, עבודות, לפני ואחרי" />
            </Field>
            <Field label="תיאור לתמונות" hint="ישמש כטקסט חלופי (alt) לכל התמונות שיועלו עכשיו">
              <TextInput maxLength={300} value={data.description} onChange={(e) => setData("description", e.target.value)} placeholder="למשל: חזית העסק בשעות הערב" />
            </Field>
            <button disabled={processing} className="btn-primary bg-brand-600">הוספה לגלריה</button>
          </form>
        </SectionCard>

        <SectionCard title="סידור תמונות" description="גרירה עדיין לא, אבל אפשר להעלות סדר ולמחוק">
          {gallery_images.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
              אין עדיין תמונות בגלריה
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {gallery_images.map((image, index) => (
                <article key={image.id} className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
                  <button type="button" onClick={() => setLightboxIndex(index)} className="block w-full">
                    <img src={image.url} alt={image.description || card.title} className="h-48 w-full object-cover" />
                  </button>
                  <div className="space-y-3 p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm text-slate-500">#{image.position + 1}</div>
                      <Badge tone="slate">{image.category}</Badge>
                    </div>
                    <div className="flex gap-2">
                      <TextInput
                        maxLength={80}
                        value={categoryDrafts[image.id] ?? image.category}
                        onChange={(event) => setCategoryDrafts((drafts) => ({ ...drafts, [image.id]: event.target.value }))}
                        placeholder="קטגוריה"
                      />
                      <TextInput
                        maxLength={300}
                        value={descriptionDrafts[image.id] ?? image.description ?? ""}
                        onChange={(event) => setDescriptionDrafts((drafts) => ({ ...drafts, [image.id]: event.target.value }))}
                        placeholder="תיאור לתמונה (alt)"
                      />
                      <button
                        type="button"
                        onClick={() => router.patch(`${galleryPath}/${image.id}`, { category: categoryDrafts[image.id], description: descriptionDrafts[image.id] }, { preserveScroll: true })}
                        className="btn-secondary whitespace-nowrap px-3 py-2 text-xs"
                      >
                        שמירה
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => router.patch(`${galleryPath}/${image.id}/move-up`, {}, { preserveScroll: true })}
                        className="btn-secondary px-3 py-2 text-xs"
                      >
                        למעלה
                      </button>
                      <button
                        type="button"
                        onClick={() => router.patch(`${galleryPath}/${image.id}/move-down`, {}, { preserveScroll: true })}
                        className="btn-secondary px-3 py-2 text-xs"
                      >
                        למטה
                      </button>
                      <button
                        type="button"
                        onClick={() => router.delete(`${galleryPath}/${image.id}`, { preserveScroll: true })}
                        className="btn-secondary px-3 py-2 text-xs text-rose-700"
                      >
                        מחיקה
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      {lightboxIndex !== null ? (
        <GalleryLightbox
          images={gallery_images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      ) : null}
    </AppLayout>
  )
}
